import React, { Component } from 'react';
import http from '../../modules/api';

const asNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const firstText = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim()) {
      return String(value).trim();
    }
  }
  return '';
};

export const normalizeInstagramAudioAsset = (raw, fallbackType = 'music') => {
  const source = raw || {};
  const durationMsRaw =
    source.duration_ms ?? source.duration_in_ms ?? source.durationMs ?? source.audio_duration_ms ?? null;
  const durationSecondsRaw =
    source.duration ?? source.duration_seconds ?? source.durationSeconds ?? null;

  let durationMs = asNumber(durationMsRaw, null);
  if (durationMs === null) {
    const seconds = asNumber(durationSecondsRaw, null);
    durationMs = seconds === null ? null : Math.round(seconds * 1000);
  }

  const normalizedType = firstText(
    source.audio_type,
    source.audioType,
    fallbackType
  ).toLowerCase();

  return {
    id: firstText(source.id, source.audio_id, source.audioId, source.ig_audio_id),
    audioType: normalizedType === 'original_sound' ? 'ORIGINAL_SOUND' : 'MUSIC',
    title: firstText(
      source.title,
      source.name,
      source.audio_name,
      source.audioName,
      source.original_audio_title,
      normalizedType === 'original_sound' ? '원본 오디오' : '제목 없음'
    ),
    artist: firstText(
      typeof source.artist === 'string' ? source.artist : null,
      source.display_artist,
      source.displayArtist,
      source.artist_name,
      source.artistName,
      source.creator,
      source.creator_name,
      source.creatorName,
      source.ig_username,
      source.username,
      source.owner_name,
      source.ownerName,
      source.ig_artist && (source.ig_artist.display_name || source.ig_artist.username || source.ig_artist.name),
      source.artist && typeof source.artist === 'object' && (source.artist.display_name || source.artist.username || source.artist.name),
      '-' 
    ),
    durationMs,
    previewUrl: firstText(
      source.download_url,
      source.downloadUrl,
      source.progressive_download_url,
      source.preview_url,
      source.previewUrl,
      source.audio_url,
      source.audioUrl,
      source.url
    ),
    platformPreviewUrl: firstText(
      source.on_platform_audio_preview_link,
      source.onPlatformAudioPreviewLink,
      source.platform_preview_url,
      source.platformPreviewUrl
    ),
    artworkUrl: firstText(
      source.cover_art_url,
      source.coverArtUrl,
      source.artwork_url,
      source.artworkUrl,
      source.thumbnail_url,
      source.thumbnailUrl,
      source.image_url,
      source.imageUrl
    ),
    raw: source,
  };
};

const extractList = (response) => {
  const root = response && response.data ? response.data : {};
  const payload = root && root.data !== undefined ? root.data : root;

  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.audio)) return payload.audio;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.list)) return payload.list;
  if (root && Array.isArray(root.audio)) return root.audio;
  if (root && Array.isArray(root.list)) return root.list;
  return [];
};

const extractNextCursor = (response) => {
  const root = response && response.data ? response.data : {};
  const payload = root && root.data !== undefined ? root.data : root;
  const paging =
    (payload && !Array.isArray(payload) && payload.paging)
    || (root && root.paging)
    || {};
  const cursors = paging && paging.cursors ? paging.cursors : {};

  return firstText(
    cursors.after,
    paging.after,
    payload && payload.after,
    root && root.after
  );
};

const extractAsset = (response) => {
  const root = response && response.data ? response.data : {};
  const payload = root && root.data !== undefined ? root.data : root;

  if (payload && !Array.isArray(payload) && typeof payload === 'object') {
    if (payload.audio && !Array.isArray(payload.audio) && typeof payload.audio === 'object') {
      return payload.audio;
    }
    return payload;
  }

  return {};
};

const formatDuration = (durationMs) => {
  const ms = asNumber(durationMs, null);
  if (ms === null || ms < 0) return '-';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

class InstagramAudioSelectModal extends Component {
  constructor(props) {
    super(props);
    this.modalRef = React.createRef();
    this.previewAudioRef = React.createRef();
    this.audioListRef = React.createRef();
    this.searchTimer = null;
    this.previewNoticeTimer = null;
    this.dragContext = null;
    this.audioListGeneration = 0;
    this.loadMoreInFlight = false;

    this.state = {
      audioType: props.selectedAudio && props.selectedAudio.audioType === 'ORIGINAL_SOUND'
        ? 'original_sound'
        : 'music',
      searchQuery: '',
      items: [],
      selectedId: props.selectedAudio ? String(props.selectedAudio.id || props.selectedAudio.audioId || '') : '',
      selectedItem: props.selectedAudio || null,
      isLoading: false,
      isLoadingMore: false,
      nextCursor: '',
      hasMore: true,
      errorMessage: '',
      playingId: '',
      previewLoadingId: '',
      previewNotice: null,
      dragX: 0,
      dragY: 0,
    };
  }

  componentDidMount() {
    if (this.props.isOpen) this.loadAudioList();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      const selected = this.props.selectedAudio || null;
      const audioType = selected && selected.audioType === 'ORIGINAL_SOUND' ? 'original_sound' : 'music';
      this.setState({
        audioType,
        searchQuery: '',
        selectedId: selected ? String(selected.id || selected.audioId || '') : '',
        selectedItem: selected,
        isLoadingMore: false,
        nextCursor: '',
        hasMore: true,
        errorMessage: '',
        playingId: '',
        previewLoadingId: '',
        previewNotice: null,
        dragX: 0,
        dragY: 0,
      }, this.loadAudioList);
    }

    if (prevProps.instagramAccessTokenSeq !== this.props.instagramAccessTokenSeq && this.props.isOpen) {
      this.stopPreview();
      this.setState({
        selectedId: '',
        selectedItem: null,
        searchQuery: '',
        items: [],
        isLoadingMore: false,
        nextCursor: '',
        hasMore: true,
      }, this.loadAudioList);
    }
  }

  componentWillUnmount() {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    if (this.previewNoticeTimer) window.clearTimeout(this.previewNoticeTimer);
    this.stopPreview();
    this.stopModalDrag();
  }

  loadAudioList = async (append = false) => {
    const accountSeq = String(this.props.instagramAccessTokenSeq || '').trim();
    const { audioType, searchQuery, nextCursor, hasMore, isLoading, isLoadingMore } = this.state;
    const keyword = String(searchQuery || '').trim();

    if (!accountSeq) {
      this.audioListGeneration += 1;
      this.loadMoreInFlight = false;
      this.setState({
        items: [],
        isLoading: false,
        isLoadingMore: false,
        nextCursor: '',
        hasMore: false,
        errorMessage: 'Instagram 계정을 먼저 선택해주세요.',
      });
      return;
    }

    if (append) {
      if (!hasMore || !nextCursor || isLoading || isLoadingMore || this.loadMoreInFlight) return;
      this.loadMoreInFlight = true;
    }

    const generation = append ? this.audioListGeneration : (this.audioListGeneration += 1);

    if (!append) {
      this.loadMoreInFlight = false;
      this.setState({
        isLoading: true,
        isLoadingMore: false,
        nextCursor: '',
        hasMore: true,
        errorMessage: '',
      }, () => {
        const list = this.audioListRef.current;
        if (list) list.scrollTop = 0;
      });
    } else {
      this.setState({ isLoadingMore: true });
    }

    try {
      const params = { audio_type: audioType };
      if (keyword) params.search_query = keyword;
      if (append && nextCursor) params.after = nextCursor;

      const response = await http.get(`/rest/shop/instagram/${accountSeq}/audio`, { params });

      // 검색어/탭/계정이 바뀐 뒤 도착한 이전 응답은 버린다.
      if (generation !== this.audioListGeneration) return;

      let rows = extractList(response).map((item) => normalizeInstagramAudioAsset(item, audioType));
      if (keyword) {
        const normalizedKeyword = keyword.toLowerCase();
        rows = rows.filter((item) =>
          String(item.title || '').toLowerCase().includes(normalizedKeyword)
          || String(item.artist || '').toLowerCase().includes(normalizedKeyword)
        );
      }

      const validRows = rows.filter((item) => item.id);
      const after = extractNextCursor(response);

      this.setState((prevState) => {
        const merged = append ? [...prevState.items, ...validRows] : validRows;
        const seen = new Set();
        const deduped = merged.filter((item) => {
          const id = String(item.id || '');
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        return {
          items: deduped,
          isLoading: false,
          isLoadingMore: false,
          nextCursor: after,
          hasMore: !!after && (!append || after !== nextCursor),
          errorMessage: '',
        };
      }, () => {
        this.loadMoreInFlight = false;

        // 페이지를 추가했는데도 스크롤이 생기지 않을 정도로 짧으면 다음 페이지를 더 이어서 채운다.
        if (this.state.hasMore) {
          window.setTimeout(this.maybeLoadMoreForShortList, 0);
        }
      });
    } catch (error) {
      if (generation !== this.audioListGeneration) return;

      console.error(append ? 'Instagram 오디오 추가 목록 조회 실패:' : 'Instagram 오디오 목록 조회 실패:', error);
      const serverMessage = error && error.response && error.response.data
        ? (error.response.data.message || error.response.data.error)
        : '';

      if (append) {
        // 추가 페이지 실패는 기존 목록을 유지한다.
        this.setState({
          isLoadingMore: false,
          errorMessage: '',
        });
      } else {
        this.setState({
          items: [],
          isLoading: false,
          isLoadingMore: false,
          nextCursor: '',
          hasMore: false,
          errorMessage: serverMessage || 'Instagram 음악 목록을 불러오지 못했습니다.',
        });
      }
      this.loadMoreInFlight = false;
    }
  };

  maybeLoadMoreForShortList = () => {
    const list = this.audioListRef.current;
    if (!list || !this.state.hasMore || this.state.isLoading || this.state.isLoadingMore) return;

    // 목록 자체가 스크롤되지 않을 정도로 짧으면 다음 페이지를 자동으로 이어서 채운다.
    if (list.scrollHeight <= list.clientHeight + 24) {
      this.loadAudioList(true);
    }
  };

  handleListScroll = (event) => {
    const target = event.currentTarget;
    if (!target || !this.state.hasMore || this.state.isLoading || this.state.isLoadingMore) return;

    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (distanceToBottom <= 140) {
      this.loadAudioList(true);
    }
  };

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.audioListGeneration += 1;
    this.loadMoreInFlight = false;
    this.setState({
      searchQuery: value,
      items: [],
      isLoading: true,
      isLoadingMore: false,
      nextCursor: '',
      hasMore: true,
      errorMessage: '',
    });
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.loadAudioList(false), 350);
  };

  handleTabChange = (audioType) => {
    if (audioType === this.state.audioType) return;
    this.clearPreviewNotice();
    this.stopPreview();
    this.audioListGeneration += 1;
    this.loadMoreInFlight = false;
    this.setState({
      audioType,
      searchQuery: '',
      items: [],
      isLoadingMore: false,
      nextCursor: '',
      hasMore: true,
      errorMessage: '',
    }, () => this.loadAudioList(false));
  };

  handleSelect = (item) => {
    this.setState({ selectedId: item.id, selectedItem: item }, () => {
      // 목록 API에는 직접 재생 URL이 없을 수 있으므로, 선택 시 상세 API로 한 번만 확인한다.
      if (item && !item.previewUrl && !item.previewChecked) {
        this.loadPreviewAsset(item);
      }
    });
  };

  stopPreview = () => {
    const audio = this.previewAudioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch (e) {
        // noop
      }
    }
    if (this.state && this.state.playingId) this.setState({ playingId: '' });
  };

  clearPreviewNotice = () => {
    if (this.previewNoticeTimer) {
      window.clearTimeout(this.previewNoticeTimer);
      this.previewNoticeTimer = null;
    }
    if (this.state && this.state.previewNotice) {
      this.setState({ previewNotice: null });
    }
  };

  showPreviewNotice = (item, triggerRect, message) => {
    if (this.previewNoticeTimer) window.clearTimeout(this.previewNoticeTimer);

    const rect = triggerRect || null;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const preferredLeft = rect ? rect.left - 10 : Math.max(16, viewportWidth - 330);
    const top = rect ? (rect.top + rect.height / 2) : 120;

    this.setState({
      previewNotice: {
        itemId: item ? item.id : '',
        message,
        platformPreviewUrl: item ? item.platformPreviewUrl : '',
        top,
        left: Math.max(16, preferredLeft),
      },
    });

    this.previewNoticeTimer = window.setTimeout(() => {
      this.previewNoticeTimer = null;
      this.setState({ previewNotice: null });
    }, 4000);
  };

  loadPreviewAsset = async (item) => {
    if (!item || !item.id) return item;
    if (item.previewUrl || item.previewChecked) return item;

    const accountSeq = String(this.props.instagramAccessTokenSeq || '').trim();
    if (!accountSeq) return item;

    this.setState({ previewLoadingId: item.id, errorMessage: '' });

    try {
      const response = await http.get(
        `/rest/shop/instagram/${accountSeq}/audio/${encodeURIComponent(item.id)}`
      );
      const detail = normalizeInstagramAudioAsset(
        extractAsset(response),
        item.audioType === 'ORIGINAL_SOUND' ? 'original_sound' : 'music'
      );

      const resolved = {
        ...item,
        ...detail,
        id: detail.id || item.id,
        title: detail.title && detail.title !== '제목 없음' ? detail.title : item.title,
        artist: detail.artist && detail.artist !== '-' ? detail.artist : item.artist,
        durationMs: detail.durationMs != null ? detail.durationMs : item.durationMs,
        previewUrl: detail.previewUrl || item.previewUrl || '',
        platformPreviewUrl: detail.platformPreviewUrl || item.platformPreviewUrl || '',
        previewChecked: true,
        raw: { ...(item.raw || {}), ...(detail.raw || {}) },
      };

      this.setState((prev) => ({
        items: prev.items.map((row) => String(row.id) === String(item.id) ? resolved : row),
        selectedItem: prev.selectedItem && String(prev.selectedItem.id) === String(item.id)
          ? resolved
          : prev.selectedItem,
        previewLoadingId: '',
      }));

      return resolved;
    } catch (error) {
      console.error('Instagram 오디오 상세 조회 실패:', error);
      const unresolved = { ...item, previewChecked: true };
      this.setState((prev) => ({
        items: prev.items.map((row) => String(row.id) === String(item.id) ? unresolved : row),
        selectedItem: prev.selectedItem && String(prev.selectedItem.id) === String(item.id)
          ? unresolved
          : prev.selectedItem,
        previewLoadingId: '',
      }));
      return unresolved;
    }
  };

  togglePreview = async (item, event) => {
    const triggerRect = event && event.currentTarget && event.currentTarget.getBoundingClientRect
      ? event.currentTarget.getBoundingClientRect()
      : null;
    if (event) event.stopPropagation();
    const audio = this.previewAudioRef.current;
    if (!audio || !item || !item.id) return;

    if (this.state.playingId === item.id && !audio.paused) {
      audio.pause();
      this.clearPreviewNotice();
      this.setState({ playingId: '' });
      return;
    }

    const resolved = item.previewUrl ? item : await this.loadPreviewAsset(item);
    if (!resolved || !resolved.previewUrl) {
      this.setState({ playingId: '' });
      this.showPreviewNotice(
        resolved || item,
        triggerRect,
        resolved && resolved.platformPreviewUrl
          ? 'Instagram API에서 직접 재생 URL을 제공하지 않았습니다.'
          : '이 음악은 현재 미리듣기를 제공하지 않습니다.'
      );
      return;
    }

    try {
      audio.pause();
      audio.src = resolved.previewUrl;
      audio.currentTime = 0;
      await audio.play();
      this.clearPreviewNotice();
      this.setState({ playingId: resolved.id, errorMessage: '' });
    } catch (error) {
      console.error('Instagram 오디오 미리듣기 실패:', error);
      this.setState({ playingId: '' });
      this.showPreviewNotice(
        resolved,
        triggerRect,
        resolved.platformPreviewUrl
          ? '브라우저에서 바로 재생할 수 없습니다.'
          : '이 음악은 현재 미리듣기를 재생할 수 없습니다.'
      );
    }
  };

  openPlatformPreview = (item, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!item || !item.platformPreviewUrl) return;
    this.clearPreviewNotice();
    window.open(item.platformPreviewUrl, '_blank', 'noopener,noreferrer');
  };

  handleApply = () => {
    const selected = this.state.selectedItem;
    if (!selected || !selected.id) return;
    this.stopPreview();
    if (this.props.onApply) this.props.onApply(selected);
    if (this.props.onClose) this.props.onClose();
  };

  handleClose = () => {
    this.stopPreview();
    if (this.props.onClose) this.props.onClose();
  };

  startModalDrag = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button, a, input, textarea, select, label')) return;
    const modal = this.modalRef.current;
    if (!modal) return;
    const rect = modal.getBoundingClientRect();
    this.dragContext = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startDragX: this.state.dragX || 0,
      startDragY: this.state.dragY || 0,
      startRect: rect,
    };
    document.addEventListener('mousemove', this.handleModalDrag);
    document.addEventListener('mouseup', this.stopModalDrag);
    e.preventDefault();
  };

  handleModalDrag = (e) => {
    const drag = this.dragContext;
    if (!drag) return;
    const rawDx = e.clientX - drag.startClientX;
    const rawDy = e.clientY - drag.startClientY;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const margin = 6;
    const horizontalOverflow = drag.startRect.width * 0.65;
    const minLeft = margin - horizontalOverflow;
    const maxLeft = vw - drag.startRect.width + horizontalOverflow - margin;
    const nextLeft = Math.min(maxLeft, Math.max(minLeft, drag.startRect.left + rawDx));
    const nextTop = Math.min(Math.max(margin, vh - 40), Math.max(margin, drag.startRect.top + rawDy));
    this.setState({
      dragX: drag.startDragX + (nextLeft - drag.startRect.left),
      dragY: drag.startDragY + (nextTop - drag.startRect.top),
    });
  };

  stopModalDrag = () => {
    document.removeEventListener('mousemove', this.handleModalDrag);
    document.removeEventListener('mouseup', this.stopModalDrag);
    this.dragContext = null;
  };

  renderAudioItem = (item) => {
    const selected = String(item.id) === String(this.state.selectedId || '');
    const playing = this.state.playingId === item.id;
    return (
      <div
        key={item.id}
        className={`instagram-audio-item ${selected ? 'selected' : ''}`}
        onClick={() => this.handleSelect(item)}
      >
        <label className="instagram-audio-checkbox" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => this.handleSelect(item)}
          />
          <span />
        </label>

        <div className="instagram-audio-artwork">
          {item.artworkUrl ? <img src={item.artworkUrl} alt="" /> : <span>♫</span>}
        </div>

        <div className="instagram-audio-info">
          <strong title={item.title}>{item.title}</strong>
          <span className="instagram-audio-artist" title={item.artist}>{item.artist}</span>
          <div className="instagram-audio-meta">
            <span>{item.audioType === 'MUSIC' ? 'Sound Collection' : '원본 오디오'}</span>
            <span>{formatDuration(item.durationMs)}</span>
          </div>
        </div>

        <div className="instagram-audio-preview-actions">
          {this.state.previewLoadingId === item.id ? (
            <span className="instagram-audio-preview-unavailable checking">미리듣기 가능 여부 확인 중...</span>
          ) : item.previewUrl ? (
            <button
              type="button"
              className={`instagram-audio-play ${playing ? 'playing' : ''}`}
              title={playing ? '정지' : '미리듣기'}
              onClick={(e) => this.togglePreview(item, e)}
            >
              {playing ? 'Ⅱ' : '▶'}
            </button>
          ) : item.platformPreviewUrl ? (
            <button
              type="button"
              className="instagram-audio-platform-preview"
              title="인스타그램에서 듣기"
              onClick={(e) => this.openPlatformPreview(item, e)}
            >
              인스타그램에서 듣기
            </button>
          ) : (
            <span className="instagram-audio-preview-unavailable">미리듣기 불가</span>
          )}
        </div>
      </div>
    );
  };

  render() {
    if (!this.props.isOpen) return null;
    const {
      audioType,
      searchQuery,
      items,
      isLoading,
      isLoadingMore,
      errorMessage,
      selectedId,
      dragX,
      dragY,
    } = this.state;

    return (
      <>
        <style>{`
          #InstagramAudioSelectModal { --am-mint:#00e2e6; --am-bg:#eafafb; --am-text:#202733; --am-sub:#7b8797; --am-line:#dfe5eb; font-family:inherit; }
          #InstagramAudioSelectModal.instagram-audio-overlay { position:fixed!important; inset:0!important; z-index:13030!important; display:flex!important; align-items:center!important; justify-content:center!important; padding:16px!important; background:transparent!important; pointer-events:none!important; }
          #InstagramAudioSelectModal .instagram-audio-modal { position:fixed!important; left:50%!important; top:50%!important; width:min(820px,calc(100vw - 24px))!important; height:min(780px,calc(100vh - 24px))!important; min-height:0!important; display:flex!important; flex-direction:column!important; overflow:hidden!important; margin:0!important; padding:0!important; border-radius:12px!important; background:#fff!important; box-shadow:0 8px 24px rgba(20,29,43,.24)!important; pointer-events:auto!important; }
          #InstagramAudioSelectModal .instagram-audio-header { position:relative!important; width:100%!important; min-width:0!important; height:auto!important; min-height:0!important; flex:0 0 auto!important; display:flex!important; align-items:flex-start!important; justify-content:space-between!important; gap:12px!important; padding:26px 30px 22px!important; box-sizing:border-box!important; border-bottom:1px solid #edf0f3!important; background:#fff!important; cursor:move!important; user-select:none!important; }
          #InstagramAudioSelectModal .instagram-audio-header > div:first-child { min-width:0!important; flex:1 1 auto!important; }
          #InstagramAudioSelectModal h2 { margin:0!important; color:var(--am-text)!important; font-size:22px!important; font-weight:800!important; line-height:1.25!important; overflow-wrap:anywhere!important; }
          #InstagramAudioSelectModal .instagram-audio-description { margin:7px 0 0!important; color:#8792a0!important; font-size:13px!important; line-height:1.45!important; white-space:normal!important; word-break:keep-all!important; overflow-wrap:break-word!important; }
          #InstagramAudioSelectModal .instagram-audio-close { display: flex; justify-content: center; align-items: center; width:38px!important; height:38px!important; flex:0 0 38px!important; border:0!important; border-radius:7px!important; background:transparent!important; color:#5f6873!important; font-size:30px!important; font-weight:300!important; cursor:pointer!important; }
          #InstagramAudioSelectModal .instagram-audio-close:hover { background:#f1f3f5!important; color:#111827!important; }
          #InstagramAudioSelectModal .instagram-audio-body { min-width:0!important; min-height:0!important; flex:1 1 auto!important; display:flex!important; flex-direction:column!important; margin:0!important; padding:22px 26px 18px!important; overflow-x:hidden!important; overflow-y:hidden!important; background:#fff!important; }
          #InstagramAudioSelectModal .instagram-audio-tabs { display:flex!important; gap:8px!important; margin-bottom:12px!important; }
          #InstagramAudioSelectModal .instagram-audio-tab { height:38px!important; padding:0 14px!important; border:1px solid #d8dee5!important; border-radius:6px!important; background:#fff!important; color:#596675!important; font-size:13px!important; font-weight:700!important; cursor:pointer!important; }
          #InstagramAudioSelectModal .instagram-audio-tab.active { border-color:var(--am-mint)!important; background:var(--am-bg)!important; color:#0b6e72!important; }
          #InstagramAudioSelectModal .instagram-audio-search-wrap { position:relative!important; margin-bottom:12px!important; }
          #InstagramAudioSelectModal .instagram-audio-search-wrap i { position:absolute!important; left:13px!important; top:50%!important; transform:translateY(-50%)!important; color:#a2aab4!important; pointer-events:none!important; }
          #InstagramAudioSelectModal .instagram-audio-search { width:100%!important; height:50px!important; padding:0 14px 0 39px!important; border:1px solid #d8dee5!important; border-radius:6px!important; background:#fff!important; color:#27303d!important; font-size:14px!important; outline:none!important; cursor: pointer; }
          #InstagramAudioSelectModal .instagram-audio-search:focus { border-color:var(--am-mint)!important; box-shadow:0 0 0 2px rgba(69,210,212,.12)!important; }
          #InstagramAudioSelectModal .instagram-audio-list { min-width:0!important; min-height:0!important; flex:1 1 auto!important; display:flex!important; flex-direction:column!important; gap:10px!important; width:100%!important; padding-right:3px!important; overflow-x:hidden!important; overflow-y:auto!important; }
          #InstagramAudioSelectModal .instagram-audio-item { display:grid!important; grid-template-columns:22px 52px minmax(0,1fr) 150px!important; align-items:center!important; gap:12px!important; width:100%!important; min-width:0!important; max-width:100%!important; padding:12px 14px!important; border:1px solid var(--am-line)!important; border-radius:9px!important; background:#fff!important; cursor:pointer!important; }
          #InstagramAudioSelectModal .instagram-audio-item:hover { border-color:#8ee3e5!important; }
          #InstagramAudioSelectModal .instagram-audio-item.selected { padding:11px 13px 11px 10px!important; border:2px solid var(--am-mint)!important; border-left-width:4px!important; background:var(--am-bg)!important; }
          #InstagramAudioSelectModal .instagram-audio-checkbox { position:relative!important; width:18px!important; height:18px!important; cursor:pointer!important; }
          #InstagramAudioSelectModal .instagram-audio-checkbox input { position:absolute!important; opacity:0!important; pointer-events:none!important; }
          #InstagramAudioSelectModal .instagram-audio-checkbox span { width:18px!important; height:18px!important; display:block!important; border:1px solid #c6ced7!important; border-radius:50%!important; background:#fff!important; }
          #InstagramAudioSelectModal .instagram-audio-checkbox input:checked + span { border-color:var(--am-mint)!important; background:var(--am-mint)!important; box-shadow:inset 0 0 0 4px #fff!important; }
          #InstagramAudioSelectModal .instagram-audio-artwork { width:52px!important; height:52px!important; display:flex!important; align-items:center!important; justify-content:center!important; overflow:hidden!important; border-radius:7px!important; background:#f1f5f9!important; color:#64748b!important; font-size:22px!important; }
          #InstagramAudioSelectModal .instagram-audio-artwork img { width:100%!important; height:100%!important; object-fit:cover!important; }
          #InstagramAudioSelectModal .instagram-audio-info { min-width:0!important; }
          #InstagramAudioSelectModal .instagram-audio-info strong { display:block!important; overflow:hidden!important; color:#202733!important; font-size:15px!important; font-weight:800!important; text-overflow:ellipsis!important; white-space:nowrap!important; }
          #InstagramAudioSelectModal .instagram-audio-artist { display:block!important; margin-top:3px!important; overflow:hidden!important; color:#6f7b89!important; font-size:12px!important; text-overflow:ellipsis!important; white-space:nowrap!important; }
          #InstagramAudioSelectModal .instagram-audio-meta { display:flex!important; gap:7px!important; margin-top:7px!important; }
          #InstagramAudioSelectModal .instagram-audio-meta span { display:flex!important; align-items:center!important; padding:4px 7px!important; border-radius:4px!important; background:#f1f5f9!important; color:#475569!important; font-size:10px!important; font-weight:600!important; vertical-align:center!important; }
          #InstagramAudioSelectModal .instagram-audio-play { width:40px!important; height:40px!important; border:1px solid #d8dee5!important; border-radius:50%!important; background:#fff!important; color:#334155!important; cursor:pointer!important; font-size:13px!important; }
          #InstagramAudioSelectModal .instagram-audio-play:hover { border-color: #8ee3e5 !important; color:#0b6e72!important; background: #f0fdfe !important; }
          #InstagramAudioSelectModal .instagram-audio-play.playing { border-color:var(--am-mint)!important; background:var(--am-bg)!important; color:#0b6e72!important; }
          #InstagramAudioSelectModal .instagram-audio-play:disabled { opacity:.35!important; cursor:not-allowed!important; }
          #InstagramAudioSelectModal .instagram-audio-platform-preview { max-width:100%!important; padding:4px 2px!important; border:0!important; background:transparent!important; color:#334155!important; font-size:10px!important; font-weight:700!important; line-height:1.3!important; white-space:nowrap!important; cursor:pointer!important; text-decoration:none!important; border: 1px solid #d8dee5 !important; border-radius: 6px !important; padding: 8px 10px !important; background: #fff !important; }
          #InstagramAudioSelectModal .instagram-audio-platform-preview:hover { border-color: #8ee3e5 !important; color:#0b6e72!important; background: #f0fdfe !important; }
          #InstagramAudioSelectModal .instagram-audio-preview-actions { width:150px!important; min-width:150px!important; display:flex!important; flex-direction:column!important; align-items:center!important; justify-content:center!important; flex:0 0 150px!important; overflow:hidden!important; }
          #InstagramAudioSelectModal .instagram-audio-preview-unavailable { display:inline-flex!important; align-items:center!important; justify-content:center!important; min-height:22px!important; padding:4px 7px!important; border-radius:4px!important; background:#f1f5f9!important; color:#64748b!important; font-size:10px!important; font-weight:700!important; line-height:1.1!important; white-space:nowrap!important; }
          #InstagramAudioSelectModal .instagram-audio-preview-unavailable.checking { color:#8792a0!important; }
          #InstagramAudioSelectModal .instagram-audio-preview-notice { position:fixed!important; z-index:13040!important; width:min(310px,calc(100vw - 32px))!important; padding:10px 12px!important; border-radius:8px!important; background:#202733!important; color:#fff!important; box-shadow:0 6px 18px rgba(15,23,42,.22)!important; font-size:12px!important; line-height:1.45!important; transform:translate(-100%,-50%)!important; pointer-events:auto!important; }
          #InstagramAudioSelectModal .instagram-audio-preview-notice::after { content:''!important; position:absolute!important; right:-6px!important; top:50%!important; width:12px!important; height:12px!important; background:#202733!important; transform:translateY(-50%) rotate(45deg)!important; }
          #InstagramAudioSelectModal .instagram-audio-preview-notice-text { position:relative!important; z-index:1!important; display:block!important; }
          #InstagramAudioSelectModal .instagram-audio-preview-notice-link { position:relative!important; z-index:1!important; display:inline-flex!important; align-items:center!important; margin-top:7px!important; padding:0!important; border:0!important; background:transparent!important; color:var(--am-mint)!important; font-size:11px!important; font-weight:700!important; cursor:pointer!important; text-decoration:underline!important; }
          #InstagramAudioSelectModal .instagram-audio-empty { display:flex!important; align-items:center!important; justify-content:center!important; flex: 1 1 auto !important; min-height: 0 !important; padding:28px!important; border:1px dashed #cfd6de!important; border-radius:8px!important; background:#fafbfc!important; color:#7d8793!important; font-size:14px!important; text-align:center!important; }
          #InstagramAudioSelectModal .instagram-audio-load-more { flex:0 0 auto!important; display:flex!important; align-items:center!important; justify-content:center!important; min-height:44px!important; padding:10px 8px!important; color:#7b8797!important; font-size:12px!important; font-weight:600!important; text-align:center!important; }
          #InstagramAudioSelectModal .instagram-audio-load-more i { margin-right:7px!important; }
          #InstagramAudioSelectModal .instagram-audio-footer { flex:0 0 80px!important; min-width:0!important; min-height:80px!important; width:100%!important; display:flex!important; align-items:center!important; justify-content:flex-end!important; gap:8px!important; padding:12px 20px!important; overflow:hidden!important; border-top:1px solid #edf0f3!important; background:#fff!important; }
          #InstagramAudioSelectModal .instagram-audio-button { height:46px!important; padding:0 18px!important; border-radius:5px!important; font-size:14px!important; font-weight:700!important; cursor:pointer!important; }
          #InstagramAudioSelectModal .instagram-audio-button.cancel { border:1px solid #d3dae1!important; background:#fff!important; color:#3f4a57!important; }
          #InstagramAudioSelectModal .instagram-audio-button.apply { border:1px solid var(--am-mint)!important; background:var(--am-mint)!important; color:#0a1a2f!important; }
          #InstagramAudioSelectModal .instagram-audio-button.apply:disabled { border-color:#dbe2e8!important; background:#dbe2e8!important; color:#fff!important; cursor:not-allowed!important; }
          @media(max-width:660px){
            #InstagramAudioSelectModal .instagram-audio-modal{width:calc(100vw - 12px)!important;height:calc(100vh - 12px)!important;}
            #InstagramAudioSelectModal .instagram-audio-header{gap:8px!important;padding:20px 18px 16px!important;}
            #InstagramAudioSelectModal h2{font-size:21px!important;}
            #InstagramAudioSelectModal .instagram-audio-description{margin-top:5px!important;font-size:13px!important;line-height:1.5!important;}
            #InstagramAudioSelectModal .instagram-audio-close{width:34px!important;height:34px!important;flex-basis:34px!important;font-size:27px!important;}
            #InstagramAudioSelectModal .instagram-audio-body{padding:16px 14px 14px!important;}
            #InstagramAudioSelectModal .instagram-audio-item{grid-template-columns:22px 44px minmax(0,1fr) 96px!important;}
            #InstagramAudioSelectModal .instagram-audio-preview-actions{width:96px!important;min-width:96px!important;flex-basis:96px!important;}
            #InstagramAudioSelectModal .instagram-audio-platform-preview{font-size:9px!important;}
          }
        `}</style>

        <div id="InstagramAudioSelectModal" className="instagram-audio-overlay">
          <section
            ref={this.modalRef}
            className="instagram-audio-modal"
            data-instagram-tutorial="post-audio-modal"
            data-instagram-tutorial-popup="POPUP_REELS_AUDIO"
            role="dialog"
            aria-modal="false"
            aria-labelledby="instagramAudioSelectTitle"
            style={{ transform: `translate(calc(-50% + ${dragX}px), calc(-50% + ${dragY}px))` }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header className="instagram-audio-header" onMouseDown={this.startModalDrag}>
              <div>
                <h2 id="instagramAudioSelectTitle">릴스 음악 선택</h2>
                <p className="instagram-audio-description">인스타그램 릴스에서 사용할 음악 또는 원본 오디오를 선택합니다.<br/>미리듣기가 제공되는 음악은 재생 버튼으로 들을 수 있습니다.</p>
              </div>
              <button type="button" className="instagram-audio-close" aria-label="팝업 닫기" onClick={this.handleClose}>×</button>
            </header>

            <div className="instagram-audio-body">
              <div className="instagram-audio-tabs" data-instagram-tutorial="post-audio-modal-tabs">
                <button type="button" className={`instagram-audio-tab ${audioType === 'music' ? 'active' : ''}`} onClick={() => this.handleTabChange('music')}>음악</button>
                <button type="button" className={`instagram-audio-tab ${audioType === 'original_sound' ? 'active' : ''}`} onClick={() => this.handleTabChange('original_sound')}>원본 오디오</button>
              </div>

              <div className="instagram-audio-search-wrap" data-instagram-tutorial="post-audio-modal-search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="search"
                  className="instagram-audio-search"
                  placeholder={audioType === 'music' ? '음악 제목 또는 가수 검색' : '원본 오디오 검색'}
                  value={searchQuery}
                  onChange={this.handleSearchChange}
                />
              </div>

              <div
                ref={this.audioListRef}
                className="instagram-audio-list"
                data-instagram-tutorial="post-audio-modal-list"
                onScroll={this.handleListScroll}
              >
                {isLoading ? (
                  <div className="instagram-audio-empty">Instagram 오디오를 불러오는 중입니다.</div>
                ) : errorMessage ? (
                  <div className="instagram-audio-empty">{errorMessage}</div>
                ) : items.length > 0 ? (
                  <>
                    {items.map(this.renderAudioItem)}
                    {isLoadingMore ? (
                      <div className="instagram-audio-load-more" role="status">
                        <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                        다음 오디오를 불러오는 중입니다.
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="instagram-audio-empty">{String(searchQuery || '').trim() ? '검색 결과가 없습니다.' : '사용 가능한 오디오가 없습니다.'}</div>
                )}
              </div>
            </div>

            {this.state.previewNotice ? (
              <div
                className="instagram-audio-preview-notice"
                role="status"
                style={{
                  top: `${this.state.previewNotice.top}px`,
                  left: `${this.state.previewNotice.left}px`,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="instagram-audio-preview-notice-text">
                  {this.state.previewNotice.message}
                </span>
                {this.state.previewNotice.platformPreviewUrl ? (
                  <button
                    type="button"
                    className="instagram-audio-preview-notice-link"
                    onClick={(e) => this.openPlatformPreview({
                      platformPreviewUrl: this.state.previewNotice.platformPreviewUrl,
                    }, e)}
                  >
                    인스타그램에서 듣기
                  </button>
                ) : null}
              </div>
            ) : null}

            <footer className="instagram-audio-footer" data-instagram-tutorial="post-audio-modal-footer">
              <button type="button" className="instagram-audio-button cancel" onClick={this.handleClose}>취소</button>
              <button type="button" className="instagram-audio-button apply" disabled={!selectedId} onClick={this.handleApply}>적용</button>
            </footer>
          </section>
        </div>
        <audio ref={this.previewAudioRef} preload="metadata" onEnded={() => this.setState({ playingId: '' })} />
      </>
    );
  }
}

export default InstagramAudioSelectModal;
